import { useState } from 'react';
import {
  Card,
  Table,
  Tabs,
  Tag,
  Typography,
  Space,
  Button,
  Modal,
  Input,
  Switch,
  InputNumber,
  App,
  type TableColumnsType,
} from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { usePermissions } from '../../auth/store';

interface ConcernTag {
  id: string;
  slug: string;
  displayName: string;
  emoji: string | null;
  sortOrder: number;
  isActive: boolean;
}

interface LanguageOption {
  id: string;
  code: string;
  displayName: string;
  nativeName: string;
  sortOrder: number;
  isActive: boolean;
}

export function CmsPage() {
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
        Topics & languages
      </Typography.Title>
      <Card>
        <Tabs
          items={[
            { key: 'tags', label: 'Concern tags', children: <TagsTab /> },
            { key: 'languages', label: 'Languages', children: <LanguagesTab /> },
          ]}
        />
      </Card>
    </Space>
  );
}

function TagsTab() {
  const canEdit = usePermissions().can('cms.edit');
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<{ slug: string; displayName: string; emoji: string; sortOrder: number }>({
    slug: '',
    displayName: '',
    emoji: '',
    sortOrder: 0,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'cms', 'tags'],
    queryFn: async () => (await api.get<ConcernTag[]>('/admin/cms/tags')).data,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post('/admin/cms/tags', {
        slug: form.slug,
        displayName: form.displayName,
        emoji: form.emoji || undefined,
        sortOrder: form.sortOrder,
      }),
    onSuccess: () => {
      message.success('Tag created');
      setCreateOpen(false);
      setForm({ slug: '', displayName: '', emoji: '', sortOrder: 0 });
      qc.invalidateQueries({ queryKey: ['admin', 'cms', 'tags'] });
    },
    onError: () => message.error('Could not create tag'),
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ConcernTag> }) =>
      api.patch(`/admin/cms/tags/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'cms', 'tags'] }),
    onError: () => message.error('Could not update the tag. Nothing was changed.'),
  });

  const columns: TableColumnsType<ConcernTag> = [
    {
      title: 'Tag',
      render: (_: unknown, row) => (
        <Space>
          {row.emoji && <span style={{ fontSize: 18 }}>{row.emoji}</span>}
          <span style={{ fontWeight: 600 }}>{row.displayName}</span>
          <Tag>{row.slug}</Tag>
        </Space>
      ),
    },
    { title: 'Order', dataIndex: 'sortOrder', width: 80 },
    {
      title: 'Active',
      render: (_: unknown, row) => (
        // Show in-flight state on the row that's actually being patched;
        // disabled across the table while any patch is mid-flight so a
        // rapid double-toggle doesn't race.
        <Switch
          checked={row.isActive}
          loading={patch.isPending && patch.variables?.id === row.id}
          // Read-only without cms.edit — the state still shows, it just
          // cannot be changed (and the API would refuse it anyway).
          disabled={patch.isPending || !canEdit}
          onChange={(v) => patch.mutate({ id: row.id, body: { isActive: v } })}
        />
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Can permission="cms.create">
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            New tag
          </Button>
        </Can>
      </div>
      <Table rowKey="id" loading={isLoading} dataSource={data ?? []} columns={columns} pagination={false} />

      <Modal
        title="New concern tag"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => create.mutate()}
        okButtonProps={{
          disabled: form.slug.length < 2 || form.displayName.length < 2,
          loading: create.isPending,
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Input
            placeholder="slug · lowercase, dashes-only"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
          />
          <Input
            placeholder="Display name · e.g. Anxiety"
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          />
          <Input
            placeholder="Emoji (optional, e.g. 🌊)"
            value={form.emoji}
            onChange={(e) => setForm({ ...form, emoji: e.target.value })}
          />
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            value={form.sortOrder}
            onChange={(v) => v !== null && setForm({ ...form, sortOrder: v })}
            placeholder="Sort order"
          />
        </Space>
      </Modal>
    </Space>
  );
}

function LanguagesTab() {
  const canEdit = usePermissions().can('cms.edit');
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<{ code: string; displayName: string; nativeName: string; sortOrder: number }>({
    code: '',
    displayName: '',
    nativeName: '',
    sortOrder: 0,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'cms', 'languages'],
    queryFn: async () => (await api.get<LanguageOption[]>('/admin/cms/languages')).data,
  });

  const create = useMutation({
    mutationFn: () => api.post('/admin/cms/languages', form),
    onSuccess: () => {
      message.success('Language added');
      setCreateOpen(false);
      setForm({ code: '', displayName: '', nativeName: '', sortOrder: 0 });
      qc.invalidateQueries({ queryKey: ['admin', 'cms', 'languages'] });
    },
    onError: () => message.error('Could not add language'),
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<LanguageOption> }) =>
      api.patch(`/admin/cms/languages/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'cms', 'languages'] }),
    onError: () => message.error('Could not update the language. Nothing was changed.'),
  });

  const columns: TableColumnsType<LanguageOption> = [
    {
      title: 'Language',
      render: (_: unknown, row) => (
        <Space>
          <Tag>{row.code}</Tag>
          <span style={{ fontWeight: 600 }}>{row.displayName}</span>
          <span style={{ color: '#8B8B8B' }}>· {row.nativeName}</span>
        </Space>
      ),
    },
    { title: 'Order', dataIndex: 'sortOrder', width: 80 },
    {
      title: 'Active',
      render: (_: unknown, row) => (
        // Show in-flight state on the row that's actually being patched;
        // disabled across the table while any patch is mid-flight so a
        // rapid double-toggle doesn't race.
        <Switch
          checked={row.isActive}
          loading={patch.isPending && patch.variables?.id === row.id}
          // Read-only without cms.edit — the state still shows, it just
          // cannot be changed (and the API would refuse it anyway).
          disabled={patch.isPending || !canEdit}
          onChange={(v) => patch.mutate({ id: row.id, body: { isActive: v } })}
        />
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Can permission="cms.create">
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            New language
          </Button>
        </Can>
      </div>
      <Table rowKey="id" loading={isLoading} dataSource={data ?? []} columns={columns} pagination={false} />

      <Modal
        title="New language"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => create.mutate()}
        okButtonProps={{
          disabled:
            form.code.length !== 2 || form.displayName.length < 2 || form.nativeName.length < 1,
          loading: create.isPending,
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Input
            placeholder="ISO 639-1 code · e.g. mr"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().slice(0, 2) })}
          />
          <Input
            placeholder="English name · e.g. Marathi"
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          />
          <Input
            placeholder="Native name · e.g. मराठी"
            value={form.nativeName}
            onChange={(e) => setForm({ ...form, nativeName: e.target.value })}
          />
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            value={form.sortOrder}
            onChange={(v) => v !== null && setForm({ ...form, sortOrder: v })}
            placeholder="Sort order"
          />
        </Space>
      </Modal>
    </Space>
  );
}
